import React, { useState, useRef, useCallback } from 'react';
import { Button } from './button';
import { Card, CardContent } from './card';
import { Alert, AlertDescription } from './alert';
import { Progress } from './progress';
import { X, Upload, Camera } from 'lucide-react';

interface ImageUploadProps {
    onUpload: (file: File) => Promise<any>;
    onSuccess?: (result: any) => void;
    onError?: (error: string) => void;
    accept?: string;
    maxSize?: number; // in MB
    preview?: boolean;
    multiple?: boolean;
    className?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
    onUpload,
    onSuccess,
    onError,
    accept = "image/*",
    maxSize = 10,
    preview = true,
    multiple = false,
    className = "",
}) => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = useCallback(async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const file = files[0];

        // Validate file size
        if (file.size > maxSize * 1024 * 1024) {
            const errorMsg = `File size must be less than ${maxSize}MB`;
            setError(errorMsg);
            onError?.(errorMsg);
            return;
        }

        // Create preview
        if (preview && file.type.startsWith('image/')) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        }

        try {
            setUploading(true);
            setError(null);
            setProgress(0);

            // Simulate progress for better UX
            const progressInterval = setInterval(() => {
                setProgress(prev => Math.min(prev + 10, 90));
            }, 100);

            const result = await onUpload(file);

            clearInterval(progressInterval);
            setProgress(100);

            setTimeout(() => {
                setUploading(false);
                setProgress(0);
                onSuccess?.(result);
            }, 500);

        } catch (error) {
            setUploading(false);
            setProgress(0);
            const errorMsg = error instanceof Error ? error.message : 'Upload failed';
            setError(errorMsg);
            onError?.(errorMsg);
        }
    }, [onUpload, onSuccess, onError, maxSize, preview]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        handleFileSelect(e.dataTransfer.files);
    }, [handleFileSelect]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    const clearPreview = useCallback(() => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setError(null);
    }, [previewUrl]);

    return (
        <div className={`space-y-4 ${className}`}>
            <Card>
                <CardContent className="p-6">
                    <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={accept}
                            multiple={multiple}
                            onChange={(e) => handleFileSelect(e.target.files)}
                            className="hidden"
                        />

                        {previewUrl ? (
                            <div className="relative">
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    className="max-w-full max-h-48 mx-auto rounded-lg"
                                />
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    className="absolute top-2 right-2"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        clearPreview();
                                    }}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex justify-center">
                                    {uploading ? (
                                        <Camera className="w-12 h-12 text-gray-400 animate-pulse" />
                                    ) : (
                                        <Upload className="w-12 h-12 text-gray-400" />
                                    )}
                                </div>
                                <div>
                                    <p className="text-lg font-medium text-gray-700">
                                        {uploading ? 'Uploading...' : 'Drop your image here'}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        or click to browse (max {maxSize}MB)
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {uploading && (
                        <div className="mt-4">
                            <Progress value={progress} className="w-full" />
                            <p className="text-sm text-gray-500 mt-2 text-center">
                                {progress}% uploaded
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {error && (
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}
        </div>
    );
};